import React, { useState } from 'react';
import API from '../api';
import { calendarDaysUntil } from '../dates';

/**
 * The season-membership banner, in two moods.
 *
 *   PENDING — the paywall is switched on but its start date hasn't arrived.
 *             Everyone still plays free. This says so, names the deadline, and
 *             offers the pay button to anyone who'd rather settle up now.
 *   DUE     — enforcement has begun and this account hasn't paid. Blocking.
 *
 * The pending case exists because `canSubmitPicks` is TRUE all through the free
 * window, so the old single early-return hid the banner exactly when we most
 * wanted to be telling people a date was coming.
 *
 * Props: membership (from GET /membership/me), onNavigate (optional)
 */
function weekdayOf(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { weekday: 'long' });
}

function formatDeadline(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  // The viewer's own timezone, deliberately: a deadline is only useful if it's
  // in the reader's clock, and kickoff is a wall-clock moment to most people.
  return d.toLocaleString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric',
    hour: 'numeric', minute: '2-digit'
  });
}

export default function PaywallBanner({ membership, onNavigate }) {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');
  const [hidden, setHidden] = useState(false);

  if (!membership) return null;

  const pending = !!membership.paywallPending;
  const due = membership.canSubmitPicks === false;
  if (!pending && !due) return null;

  /* A comped account owes nothing, so it is not told a payment is due.
   *
   * `exempt` was in this payload the whole time and this component never read
   * it. The owner's own account is comped, and it was being shown "Payment is
   * due by Wednesday, September 16" over a Pay $10 button for money the server
   * would have refused to take: /membership/checkout answers a comped
   * non-admin with "You already have free access, no payment needed."
   *
   * Silence rather than a "you play free" badge. This banner exists to ask for
   * something, and there is nothing to ask a comped account for. The Pools page
   * still shows them a plays-free badge, which is the right place for a status
   * that is about them rather than about money.
   */
  if (membership.exempt) return null;

  const pools = membership.pools || [];
  // Nothing to say to someone already paid up everywhere.
  if (pending && pools.length > 0 && pools.every(p => p.paid)) return null;
  // The pending notice is dismissible; a blocking one is not.
  if (pending && hidden) return null;

  /* Which pool the payment is recorded against. Any of theirs will do, so take
     the one they have not already paid through, and fall back to the first. */
  const payThrough = pools.find(p => !p.paid) || pools[0];
  const seasonPrice = Math.round(
    (payThrough?.nextPurchase?.priceCents ?? 1000) / 100
  );

  const deadline = formatDeadline(membership.paywallStartsAt);
  /* Calendar days, not blocks of twenty four hours. The old arithmetic returned
     1 at two in the afternoon on kickoff day, so the banner would have said the
     season starts TOMORROW on the morning it started. */
  const days = calendarDaysUntil(membership.paywallStartsAt);
  const weekday = weekdayOf(membership.paywallStartsAt);
  /* Launch week, meaning the deadline is close enough that a date is a warning
     rather than a diary note. Derived from the date the server sends, so this
     stops being launch week on its own. The banner it replaced said "the whole
     preseason is free, start your pool now" four days before Week 1, because
     that copy was written for a window that had closed and had no way to know. */
  const soon = days !== null && days <= 8;

  const pay = async (poolId) => {
    setBusy(poolId); setErr('');
    try {
      const res = await API.post('/membership/checkout', { pool_id: poolId });
      if (res.data?.url) {
        window.location.href = res.data.url;   // hosted Stripe Checkout
      } else {
        setErr('Could not start checkout.');
        setBusy(null);
      }
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not start checkout.');
      setBusy(null);
    }
  };

  return (
    <div style={{
      marginBottom: 20,
      position: 'relative',
      background: pending
        ? 'linear-gradient(135deg, rgba(1,51,105,0.22), rgba(127,179,255,0.08))'
        : 'linear-gradient(135deg, rgba(213,10,10,0.14), rgba(1,51,105,0.12))',
      border: pending ? '1px solid rgba(127,179,255,0.35)' : '1px solid rgba(213,10,10,0.4)',
      borderRadius: 12,
      padding: '18px 20px'
    }}>
      {pending && (
        <button
          onClick={() => setHidden(true)}
          aria-label="Dismiss"
          style={{ position: 'absolute', top: 10, right: 12, background: 'none', border: 'none',
                   color: '#556', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 4 }}
        >×</button>
      )}

      <div style={{
        fontFamily: "'Oswald', sans-serif", fontSize: 15, fontWeight: 700,
        color: pending ? '#7fb3ff' : '#ff8888',
        letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 8
      }}>
        {/* Never names a week.
             This said "Week 1 kicks off Wednesday", which was true only while
             the free window happened to end at Week 1 kickoff. The moment that
             date moves, and it has, the banner would have gone on announcing
             Week 1 all through Week 1. The deadline is a date, so the headline
             talks about the date. */}
        {!pending ? '🔒 Season membership required'
          : days !== null && days <= 0 ? '🏈 Last free day'
          : days === 1 ? '🏈 Free until tomorrow'
          : soon && weekday ? `🏈 Free until ${weekday}`
          : '🏈 Free to play right now'}
      </div>

      {pending ? (
        /* Two things people need, in the order they need them: when the free
           part ends, and what actually happens to them when it does.

           The floor under it matters as much as the warning. This banner only
           renders for somebody who has not paid, and the honest reaction to
           "your entry comes off the board" is to assume the picks are gone and
           give up. They are not gone. They keep scoring the whole time, and
           paying puts the row back with the record it earned, which is the
           sentence that turns a threat into a reason.

           Once the deadline is further off than a week it goes back to being a
           recruiting line, because that is what it is at that distance. */
        <div style={{ fontSize: 14, color: '#aab', lineHeight: 1.5, marginBottom: 14 }}>
          {soon ? (
            <>
              {deadline
                ? <>The free run ends <strong style={{ color: '#fff' }}>{deadline}</strong>.</>
                : <>The free run ends when the season gets going.</>}
              {' '}Nothing gets deleted. Your picks stay saved and keep scoring, but an unpaid entry
              comes off the standings and cannot add or change a pick until the season is paid for.
              Pay and you are back on the board with the record you earned.
            </>
          ) : (
            <>
              Everything is free right now, so set your pool up and invite people while it is, and
              your league is already going by the time it counts.
              {deadline
                ? <> Payment is due by <strong style={{ color: '#fff' }}>{deadline}</strong>.</>
                : <> Payment is due once the free window closes.</>}
            </>
          )}
          {' '}<strong style={{ color: '#fff' }}>$10 covers the whole {membership.season} season</strong>,
          Week 1 through the Super Bowl, and it covers every pool you are in.
        </div>
      ) : (
        <div style={{ fontSize: 14, color: '#aab', lineHeight: 1.5, marginBottom: 14 }}>
          It's <strong style={{ color: '#fff' }}>$10 for the whole {membership.season} season</strong>,
          Week 1 through the Super Bowl, and one payment covers every pool you are in. Look around as
          much as you want. You just need it to lock in picks.
        </div>
      )}

      {!membership.checkoutAvailable ? (
        <div style={{ fontSize: 13, color: '#ffb347' }}>
          Payments aren't switched on yet. Hang tight.
        </div>
      ) : pools.length === 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: '#889' }}>Join a pool first, then you can pay for it.</span>
          {onNavigate && (
            <button
              onClick={() => onNavigate('pools')}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                       background: 'linear-gradient(135deg,#013369,#0a5299)', color: '#fff',
                       fontSize: 12, fontWeight: 700 }}
            >Find a pool →</button>
          )}
        </div>
      ) : (
        /* ONE button.
         *
         * This used to render a Pay button per pool, because the price was per
         * pool. Somebody in three pools was shown three buttons and a bill for
         * thirty dollars. One payment now covers the season everywhere, so
         * offering a choice of which pool to pay for is offering a choice that
         * no longer exists, and the first person to click two of them would have
         * been charged twice for the same thing.
         *
         * A pool is still named on the request, because the receipt has to point
         * at something. It is theirs, and which one makes no difference. */
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button
            onClick={() => pay(payThrough.pool_id)}
            disabled={busy !== null}
            style={{
              minHeight: 44, padding: '10px 20px', borderRadius: 8, cursor: busy !== null ? 'default' : 'pointer',
              border: 'none',
              background: busy !== null ? '#555' : 'linear-gradient(135deg,#D50A0A,#a00808)',
              color: '#fff', fontSize: 14, fontWeight: 700,
              fontFamily: "'Oswald', sans-serif", letterSpacing: 0.5
            }}
          >
            {busy !== null ? 'Opening…' : `Pay $${seasonPrice} for the season`}
          </button>
          {pools.length > 1 && (
            <span style={{ fontSize: 12, color: '#889' }}>
              Covers all {pools.length} of your pools.
            </span>
          )}
        </div>
      )}

      {err && <div style={{ marginTop: 10, fontSize: 13, color: '#ff6666' }}>{err}</div>}
    </div>
  );
}
